import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { listPartnerRfqs } from '../../api/partnerPortal';
import { formatDistanceToNowStrict } from 'date-fns';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'invited', label: 'Open' },
  { key: 'negotiating', label: 'Negotiating' },
  { key: 'committed', label: 'Committed' },
  { key: 'awarded', label: 'Awarded' },
  { key: 'declined', label: 'Declined' },
];

const formatRelative = (value) => {
  if (!value) return '—';
  try {
    return formatDistanceToNowStrict(new Date(value), { addSuffix: true });
  } catch {
    return '—';
  }
};

const PartnerRFQListPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('invited');

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['partner-rfqs', status],
    () =>
      listPartnerRfqs({
        status: status === 'all' ? undefined : status,
      }),
    {
      keepPreviousData: true,
    }
  );

  const rfqs = useMemo(() => data?.rfqs || [], [data]);

  const renderRFQCard = (item) => {
    const deadline = item.deadlines?.responseDue;
    return (
      <Paper
        key={item.id}
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {item.title}
              </Typography>
              {item.brand?.name && (
                <Chip
                  label={item.brand.name}
                  size="small"
                  variant="outlined"
                />
              )}
              {item.assignment?.status && (
                <Chip
                  label={item.assignment.status}
                  size="small"
                  color={
                    item.assignment.status === 'invited'
                      ? 'warning'
                      : item.assignment.status === 'committed'
                        ? 'success'
                        : item.assignment.status === 'awarded'
                          ? 'primary'
                          : item.assignment.status === 'declined'
                            ? 'error'
                            : 'default'
                  }
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Target units: {item.requirements?.production?.targetUnits ?? '—'} •
              Response due: {deadline ? formatRelative(deadline) : 'Not set'}
            </Typography>
            {item.campaign?.name && (
              <Typography variant="caption" color="text.secondary">
                Campaign: {item.campaign.name}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              onClick={() => navigate(`/partner/rfqs/${item.id}`)}
            >
              Review
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Requests for Quote
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review new opportunities, collaborate on production terms, and commit to campaigns.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {isFetching && <CircularProgress size={18} />}
          <Button variant="text" onClick={() => refetch()}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Tabs
        value={status}
        onChange={(_, value) => setStatus(value)}
        variant="scrollable"
        scrollButtons="auto"
      >
        {STATUS_TABS.map((tab) => (
          <Tab key={tab.key} value={tab.key} label={tab.label} />
        ))}
      </Tabs>

      {error && (
        <Alert severity="error">
          {error?.response?.data?.error || error?.message || 'Failed to load RFQs.'}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : rfqs.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2">
            No RFQs in this status. Keep an eye out for new opportunities from Studio Sivad.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>{rfqs.map(renderRFQCard)}</Stack>
      )}
    </Box>
  );
};

export default PartnerRFQListPage;
